import { c as _c } from "react/compiler-runtime";
import { LoaderCircleIcon, LoaderIcon, LoaderPinwheelIcon, type LucideProps } from "lucide-react";
import { cn } from "@lib/utils";
type SpinnerVariantProps = Omit<SpinnerProps, "variant">;
const Default = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("animate-spin", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <LoaderIcon className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const Circle = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("animate-spin", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <LoaderCircleIcon className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const Pinwheel = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("animate-spin", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <LoaderPinwheelIcon className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const CircleFilled = t0 => {
  const $ = _c(22);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const size = t1 === undefined ? 24 : t1;
  let t2;
  if ($[4] !== size) {
    t2 = {
      width: size,
      height: size
    };
    $[4] = size;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== className) {
    t3 = cn("animate-spin", className, "text-foreground opacity-20");
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== props || $[9] !== size || $[10] !== t3) {
    t4 = <div className="absolute inset-0 rotate-180"><LoaderCircleIcon className={t3} size={size} {...props} /></div>;
    $[8] = props;
    $[9] = size;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== className) {
    t5 = cn("relative animate-spin", className);
    $[12] = className;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== props || $[15] !== size || $[16] !== t5) {
    t6 = <LoaderCircleIcon className={t5} size={size} {...props} />;
    $[14] = props;
    $[15] = size;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t2 || $[19] !== t4 || $[20] !== t6) {
    t7 = <div className="relative" style={t2}>{t4}{t6}</div>;
    $[18] = t2;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
const Ellipsis = t0 => {
  const $ = _c(10);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const size = t1 === undefined ? 24 : t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <title>Loading...</title>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <circle cx="4" cy="12" r="2" fill="currentColor"><animate id="ellipsis1" begin="0;ellipsis3.end+0.25s" attributeName="cy" calcMode="spline" dur="0.6s" values="12;6;12" keySplines=".33,.66,.66,1;.33,0,.66,.33" /></circle>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <circle cx="12" cy="12" r="2" fill="currentColor"><animate begin="ellipsis1.begin+0.1s" attributeName="cy" calcMode="spline" dur="0.6s" values="12;6;12" keySplines=".33,.66,.66,1;.33,0,.66,.33" /></circle>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <circle cx="20" cy="12" r="2" fill="currentColor"><animate id="ellipsis3" begin="ellipsis1.begin+0.2s" attributeName="cy" calcMode="spline" dur="0.6s" values="12;6;12" keySplines=".33,.66,.66,1;.33,0,.66,.33" /></circle>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== props || $[8] !== size) {
    t6 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" {...props}>{t2}{t3}{t4}{t5}</svg>;
    $[7] = props;
    $[8] = size;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
const Ring = t0 => {
  const $ = _c(9);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const size = t1 === undefined ? 24 : t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <title>Loading...</title>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <circle cx="22" cy="22" r="1"><animate attributeName="r" begin="0s" dur="1.8s" values="1; 20" calcMode="spline" keyTimes="0; 1" keySplines="0.165, 0.84, 0.44, 1" repeatCount="indefinite" /><animate attributeName="stroke-opacity" begin="0s" dur="1.8s" values="1; 0" calcMode="spline" keyTimes="0; 1" keySplines="0.3, 0.61, 0.355, 1" repeatCount="indefinite" /></circle>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <g fill="none" fillRule="evenodd" strokeWidth="2">{t3}<circle cx="22" cy="22" r="1"><animate attributeName="r" begin="-0.9s" dur="1.8s" values="1; 20" calcMode="spline" keyTimes="0; 1" keySplines="0.165, 0.84, 0.44, 1" repeatCount="indefinite" /><animate attributeName="stroke-opacity" begin="-0.9s" dur="1.8s" values="1; 0" calcMode="spline" keyTimes="0; 1" keySplines="0.3, 0.61, 0.355, 1" repeatCount="indefinite" /></circle></g>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== props || $[7] !== size) {
    t5 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 44 44" stroke="currentColor" {...props}>{t2}{t4}</svg>;
    $[6] = props;
    $[7] = size;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
const Bars = t0 => {
  const $ = _c(11);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const size = t1 === undefined ? 24 : t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <title>Loading...</title>;
    t3 = <style>{"\n      .spinner-bar {\n        animation: spinner-bars-animation .8s linear infinite;\n        animation-delay: -.8s;\n      }\n      .spinner-bars-2 {\n        animation-delay: -.65s;\n      }\n      .spinner-bars-3 {\n        animation-delay: -0.5s;\n      }\n      @keyframes spinner-bars-animation {\n        0% {\n          y: 1px;\n          height: 22px;\n        }\n        93.75% {\n          y: 5px;\n          height: 14px;\n          opacity: 0.2;\n        }\n      }\n    "}</style>;
    t4 = <rect className="spinner-bar" x="1" y="1" width="6" height="22" fill="currentColor" />;
    t5 = <rect className="spinner-bar spinner-bars-2" x="9" y="1" width="6" height="22" fill="currentColor" />;
    t6 = <rect className="spinner-bar spinner-bars-3" x="17" y="1" width="6" height="22" fill="currentColor" />;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[8] !== props || $[9] !== size) {
    t7 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" {...props}>{t2}{t3}{t4}{t5}{t6}</svg>;
    $[8] = props;
    $[9] = size;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
const Infinite = t0 => {
  const $ = _c(8);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const size = t1 === undefined ? 24 : t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <title>Loading...</title>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <path fill="none" stroke="currentColor" strokeWidth="10" strokeDasharray="205.271142578125 51.317785644531256" d="M24.3 30C11.4 30 5 43.3 5 50s6.4 20 19.3 20c19.3 0 32.1-40 51.4-40 C88.6 30 95 43.3 95 50s-6.4 20-19.3 20C56.4 70 43.6 30 24.3 30z" strokeLinecap="round" style={{
      transform: "scale(0.8)",
      transformOrigin: "50px 50px"
    }}><animate attributeName="stroke-dashoffset" repeatCount="indefinite" dur="2s" keyTimes="0;1" values="0;256.58892822265625" /></path>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== props || $[6] !== size) {
    t4 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" {...props}>{t2}{t3}</svg>;
    $[5] = props;
    $[6] = size;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export type SpinnerProps = LucideProps & {
  variant?: "default" | "circle" | "pinwheel" | "circle-filled" | "ellipsis" | "ring" | "bars" | "infinite";
};
export const Spinner = t0 => {
  const $ = _c(19);
  let props;
  let variant;
  if ($[0] !== t0) {
    ({
      variant,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = variant;
  } else {
    props = $[1];
    variant = $[2];
  }
  switch (variant) {
    case "circle":
      {
        let t1;
        if ($[3] !== props) {
          t1 = <Circle {...props} />;
          $[3] = props;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
    case "pinwheel":
      {
        let t1;
        if ($[5] !== props) {
          t1 = <Pinwheel {...props} />;
          $[5] = props;
          $[6] = t1;
        } else {
          t1 = $[6];
        }
        return t1;
      }
    case "circle-filled":
      {
        let t1;
        if ($[7] !== props) {
          t1 = <CircleFilled {...props} />;
          $[7] = props;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
    case "ellipsis":
      {
        let t1;
        if ($[9] !== props) {
          t1 = <Ellipsis {...props} />;
          $[9] = props;
          $[10] = t1;
        } else {
          t1 = $[10];
        }
        return t1;
      }
    case "ring":
      {
        let t1;
        if ($[11] !== props) {
          t1 = <Ring {...props} />;
          $[11] = props;
          $[12] = t1;
        } else {
          t1 = $[12];
        }
        return t1;
      }
    case "bars":
      {
        let t1;
        if ($[13] !== props) {
          t1 = <Bars {...props} />;
          $[13] = props;
          $[14] = t1;
        } else {
          t1 = $[14];
        }
        return t1;
      }
    case "infinite":
      {
        let t1;
        if ($[15] !== props) {
          t1 = <Infinite {...props} />;
          $[15] = props;
          $[16] = t1;
        } else {
          t1 = $[16];
        }
        return t1;
      }
    default:
      {
        let t1;
        if ($[17] !== props) {
          t1 = <Default {...props} />;
          $[17] = props;
          $[18] = t1;
        } else {
          t1 = $[18];
        }
        return t1;
      }
  }
};